import FEATURE_FLAGS from '@/constants/featureFlags';
import { PartnerRolloutFlag } from '@/constants/localFeatureFlags';
import PERMISSIONS from '@/constants/permissions';
import { SOLUTION_KEYS } from '@/constants/solutions';
import { BackofficeUserModel } from '@/typings/api/user';
import { checkUserAccessToPath, hasAccess } from '@/utils/access';
import { ROUTE_PATHS } from '../../config/RoutePaths';

// +* refers to the business partner who has access to all the permissions and * means everybody can see the page
// All lead generation parts are currently only restricted to the business partner
const routeToPermissionMap = {
  // Marketing reports only — must NOT be bare '/reports/*': matching is
  // first-pattern-wins, and the sales reports live under /reports/sales/*
  // with their own entries (and without the LEAD_GENERATION flag).
  '/reports/marketing/*': {
    permissions: [PERMISSIONS.Analytics.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  [ROUTE_PATHS.SERVICE_REQUESTS]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  [ROUTE_PATHS.LOCATION_DETAIL]: {
    permissions: [
      PERMISSIONS.Branches.EDIT,
      PERMISSIONS.Branches.CREATE,
      PERMISSIONS.Branches.DELETE,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  // The team-overview report pinned to one branch — same analytics permission
  // as TEAM_OVERVIEW.
  [ROUTE_PATHS.LOCATION_REPORT]: {
    permissions: [
      PERMISSIONS.Leads.VIEW_PERFORMANCE_CHART,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.STUDIO]: { permissions: [], featureFlags: [] },
  [ROUTE_PATHS.TAG_STATISTICS]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  [ROUTE_PATHS.WORKSPACE_SETTINGS]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  [ROUTE_PATHS.BILLINGS]: {
    permissions: [PERMISSIONS.Billing.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  [ROUTE_PATHS.BILLINGS_DETAILS]: {
    permissions: [PERMISSIONS.Billing.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  [ROUTE_PATHS.LEAD_DETAIL]: {
    permissions: [PERMISSIONS.Leads.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  // region Lead generation customers
  [ROUTE_PATHS.LEAD_GENERATION_CUSTOMERS_ROOT]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  [ROUTE_PATHS.LEAD_GENERATION_CUSTOMERS_LIST]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  [ROUTE_PATHS.LEAD_GENERATION_CUSTOMERS_SERVICE_REQUESTS]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  // endregion

  // region Content
  [ROUTE_PATHS.CONTENT_ROOT]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  [ROUTE_PATHS.CONTENT_IMAGES]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  [ROUTE_PATHS.CONTENT_PROMOTIONS]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  [ROUTE_PATHS.CONTENT_SETTINGS]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION],
  },
  // endregion

  // region Dashboard and tasks
  // The agent's personal daily dashboard — `todays_tasks_view` keeps it gated
  // once the rollout flag is dropped at release.
  [ROUTE_PATHS.MY_DAY]: {
    permissions: [PERMISSIONS.Today.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.TASKS]: {
    permissions: ['*'],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.TASK_DETAILS]: {
    permissions: ['*'],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.TASK_DETAILS_LEGACY]: {
    permissions: ['*'],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  // endregion

  // region CRM

  [ROUTE_PATHS.APPOINTMENT_DETAILS]: {
    permissions: [
      PERMISSIONS.ServiceRequests.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.CALENDAR_EVENT_DETAILS]: {
    permissions: [
      PERMISSIONS.ServiceRequests.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.CALENDAR]: {
    permissions: [
      PERMISSIONS.ServiceRequests.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.APPOINTMENTS]: {
    permissions: [
      PERMISSIONS.ServiceRequests.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.DOUBLE_BOOKINGS]: {
    permissions: [
      PERMISSIONS.ServiceRequests.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.APPOINTMENTS_STANDALONE]: {
    permissions: [
      PERMISSIONS.ServiceRequests.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.CRM_ROOT]: {
    permissions: [
      PERMISSIONS.Leads.VIEW,
      PERMISSIONS.ServiceRequests.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT, FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.LEADS]: {
    permissions: [PERMISSIONS.Leads.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.LEADS_BOARD]: {
    permissions: [PERMISSIONS.Leads.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.NOTIFICATION_CENTER]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT, FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.ORGANIZATION_EXTRA_FIELD_TYPES]: {
    permissions: [
      PERMISSIONS.ExtraFieldType.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  // endregion

  // region Organization
  [ROUTE_PATHS.ORGANIZATION_ROOT]: {
    permissions: [
      PERMISSIONS.Branches.EDIT,
      PERMISSIONS.Branches.CREATE,
      PERMISSIONS.Branches.DELETE,
      PERMISSIONS.Users.CREATE,
      PERMISSIONS.Users.EDIT,
      PERMISSIONS.Users.DELETE,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  [ROUTE_PATHS.ORGANIZATION_USER]: {
    permissions: [
      PERMISSIONS.Users.CREATE,
      PERMISSIONS.Users.EDIT,
      PERMISSIONS.Users.DELETE,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  [ROUTE_PATHS.USERS_DETAILS]: {
    permissions: [PERMISSIONS.Users.EDIT, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  [ROUTE_PATHS.ORGANIZATION_LOCATIONS]: {
    permissions: [
      PERMISSIONS.Branches.EDIT,
      PERMISSIONS.Branches.CREATE,
      PERMISSIONS.Branches.DELETE,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  [ROUTE_PATHS.ORGANIZATION_ROLES]: {
    permissions: [
      PERMISSIONS.Roles.CREATE,
      PERMISSIONS.Roles.EDIT,
      PERMISSIONS.Permissions.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  [ROUTE_PATHS.ORGANIZATION_SERVICE_TYPES]: {
    permissions: [PERMISSIONS.ServiceTypes.EDIT, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  [ROUTE_PATHS.ORGANIZATION_DATA_ENTRY_MANAGEMENT]: {
    permissions: [PERMISSIONS.PartnerConfig.EDIT, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  // endregion

  // region Lead Management Reports
  // The sales reports menu group. Every child gates itself; the group only
  // needs to open for anyone who may see at least one report — the goal
  // permissions are listed so a salesperson with `goals_view` reaches
  // Goal Planning (their other reports stay hidden by their own gates).
  [ROUTE_PATHS.REPORTS_LEAD_MANAGEMENT]: {
    permissions: [
      PERMISSIONS.Analytics.VIEW,
      PERMISSIONS.Leads.VIEW_FUNNEL_CHART,
      PERMISSIONS.Goals.VIEW,
      PERMISSIONS.Goals.VIEW_ALL,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT, FEATURE_FLAGS.SCHEDULER],
  },
  [ROUTE_PATHS.REPORTS_DASHBOARD_LEAD_MANAGEMENT]: {
    permissions: [PERMISSIONS.Analytics.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.REPORTS_FUNNEL_LEAD_MANAGEMENT]: {
    permissions: [
      PERMISSIONS.Leads.VIEW_FUNNEL_CHART,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.TEAM_PERFORMANCE]: {
    permissions: [
      PERMISSIONS.Leads.VIEW_PERFORMANCE_CHART,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  // Goal planning — gated like the Laravel GoalPolicy: any goal permission
  // opens the page (goals_view holders get a read-only view of their own
  // goals and the ones they roll up into). Goals belong to the Deal Manager
  // module; the reports group above already requires lead_management OR
  // scheduler. Per-goal editing follows the API's can_edit / can_delete.
  [ROUTE_PATHS.GOAL_PLANNING]: {
    permissions: [
      PERMISSIONS.Goals.VIEW,
      PERMISSIONS.Goals.VIEW_ALL,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.DEAL_MANAGER],
  },
  // Manager dashboard — same data sources as the team performance report.
  [ROUTE_PATHS.TEAM_OVERVIEW]: {
    permissions: [PERMISSIONS.Analytics.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.REPORTS_CALL_BACK_LEAD_MANAGEMENT]: {
    permissions: [
      PERMISSIONS.BUSINESS_PARTNER,
      PERMISSIONS.Calls.CALL_CHART_VIEW,
    ],
    featureFlags: [FEATURE_FLAGS.CALL_TRACKING],
  },
  // endregion

  '/roi': {
    permissions: [PERMISSIONS.Analytics.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  [ROUTE_PATHS.OPEN_PROPOSALS]: { permissions: [], featureFlags: [] },
  [ROUTE_PATHS.Calls]: {
    permissions: [PERMISSIONS.BUSINESS_PARTNER, PERMISSIONS.Calls.VIEW],
    featureFlags: [FEATURE_FLAGS.CALL_TRACKING],
  },
  // region Agents & Tools
  // The group root is the union of its children's permissions and carries no
  // feature flag, so it is never the thing that hides a child — each child below
  // keeps its own gate, and `filterMenu` drops the ones a user cannot see. The
  // apps gate used to live here and now sits on the pages it guards, so moving
  // the exports and automations in did not widen their access.
  [ROUTE_PATHS.INTEGRATIONS]: {
    permissions: [
      PERMISSIONS.BUSINESS_PARTNER,
      PERMISSIONS.INTEGRATIONS.MANAGE,
      PERMISSIONS.API_KEYS.VIEW,
      PERMISSIONS.Exports.VIEW,
      PERMISSIONS.EmailAutomation.VIEW,
      PERMISSIONS.TaskAutomation.VIEW,
      PERMISSIONS.TaskAutomation.MANAGE_ALL,
    ],
    featureFlags: [],
  },
  // Both keys are needed: the detail pattern also matches the bare list path,
  // but only if it is reached first, and neither should depend on that.
  [ROUTE_PATHS.INTEGRATIONS_APPS]: {
    permissions: [
      PERMISSIONS.BUSINESS_PARTNER,
      PERMISSIONS.INTEGRATIONS.MANAGE,
    ],
    featureFlags: [],
  },
  [ROUTE_PATHS.INTEGRATION_DETAILS]: {
    permissions: [
      PERMISSIONS.BUSINESS_PARTNER,
      PERMISSIONS.INTEGRATIONS.MANAGE,
    ],
    featureFlags: [],
  },
  // Moved in from the CRM and Automations groups with their gates untouched
  // (except task automations, now on their own permission) — that, plus the
  // permissive group root above, keeps who sees them the same as before the move.
  // Scheduler-only partners get the Export Center too, so both module flags
  // count here — `hasFeatureFlag` is an OR.
  [ROUTE_PATHS.EXPORT_CENTER]: {
    permissions: [PERMISSIONS.Exports.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_MANAGEMENT, FEATURE_FLAGS.SCHEDULER],
  },
  // Task automations are gated separately from email automations.
  [ROUTE_PATHS.AUTOMATION_TASK]: {
    permissions: [
      PERMISSIONS.TaskAutomation.VIEW,
      PERMISSIONS.TaskAutomation.MANAGE_ALL,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.AI_AUTOMATION],
  },
  [ROUTE_PATHS.AUTOMATION_USER]: {
    permissions: [
      PERMISSIONS.EmailAutomation.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.AI_AUTOMATION],
  },
  // endregion

  // region Touchpoints
  // The group root is the union of its children — a user holding none of the
  // touchpoint permissions gets no menu entry at all rather than a group of
  // pages that all 403. It carries no feature flag, so it never hides a child
  // that would otherwise be reachable; each child keeps its own gate and
  // `filterMenu` drops the ones the user cannot see.
  [ROUTE_PATHS.TOUCHPOINTS_ROOT]: {
    permissions: [
      PERMISSIONS.EmailTemplate.VIEW,
      PERMISSIONS.EmailFragments.MANAGE,
      PERMISSIONS.Mailers.MANAGE,
      PERMISSIONS.Branding.MANAGE,
      PERMISSIONS.PartnerTranslations.EDIT,
      PERMISSIONS.BOOKING_PAGES.MANAGE,
      PERMISSIONS.LeadSource.VIEW,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  // The three tabs behind this path each have a permission of their own
  // (EmailTemplatePolicy, TemplateFragmentPolicy, PartnerMailerPolicy), so any
  // one of them is enough to reach the page — the tabs themselves hide.
  [ROUTE_PATHS.EMAIL_CENTER]: {
    permissions: [
      PERMISSIONS.EmailTemplate.VIEW,
      PERMISSIONS.EmailFragments.MANAGE,
      PERMISSIONS.Mailers.MANAGE,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  // Branding and Translations came out of the Email Center's tabs. Reading the
  // theme and the language list stays open server-side (previews everywhere
  // need it) — it is the editing these pages exist for that is gated.
  [ROUTE_PATHS.TOUCHPOINTS_DESIGN]: {
    permissions: [PERMISSIONS.Branding.MANAGE, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  [ROUTE_PATHS.TOUCHPOINTS_TRANSLATIONS]: {
    permissions: [
      PERMISSIONS.PartnerTranslations.EDIT,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  // Booking pages were an account tab shown only with the scheduler module;
  // as a nav item the flag has to gate the item itself.
  [ROUTE_PATHS.TOUCHPOINTS_BOOKING_PAGES]: {
    permissions: [
      PERMISSIONS.BOOKING_PAGES.MANAGE,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [FEATURE_FLAGS.SCHEDULER],
  },
  // Uploading is also open to email authors (Laravel FilestorePolicy), so the
  // page itself is gated on viewing; the delete button has its own check.
  [ROUTE_PATHS.CONTENT_FILES]: {
    permissions: [
      PERMISSIONS.Files.VIEW,
      PERMISSIONS.EmailTemplate.DRAFT,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  [ROUTE_PATHS.API_KEYS]: {
    permissions: [PERMISSIONS.API_KEYS.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [],
  },
  [ROUTE_PATHS.TOUCHPOINTS_LEAD_SOURCES]: {
    permissions: [PERMISSIONS.LeadSource.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    featureFlags: [FEATURE_FLAGS.LEAD_GENERATION, FEATURE_FLAGS.LEAD_MANAGEMENT],
  },
  // endregion
  [ROUTE_PATHS.BOOKING_PAGES]: {
    permissions: [
      PERMISSIONS.BOOKING_PAGES.MANAGE,
      PERMISSIONS.BUSINESS_PARTNER,
    ],
    featureFlags: [],
  },
  [ROUTE_PATHS.ROOT]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.LOGIN]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.CHECK]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.MIMIC]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.USER_FORGOT_PASSWORD]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.RESET_PASSWORD]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.SSO_CALLBACK]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.SUPPORT]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.COMPONENTS]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.PROFILE]: { permissions: ['*'], featureFlags: [] },
  [ROUTE_PATHS.LANGUAGE]: { permissions: ['*'], featureFlags: [] },
};
export default routeToPermissionMap;

export function getPathProperties(
  path: string,
  roles?: {
    [key: string]:
      | string[]
      | {
          permissions: string[];
          featureFlags: string[];
          rolloutFlags?: PartnerRolloutFlag[];
        };
  },
): {
  permissions: string[];
  featureFlags: string[];
  rolloutFlags?: PartnerRolloutFlag[];
} {
  const mapping = roles ?? routeToPermissionMap;
  // An exact route wins over any pattern. pathMatchesPattern() allows one leading
  // segment before the pattern, so '/tasks' also matches '/automations/tasks' and,
  // being declared earlier, used to answer for it — which handed the task
  // automations page whatever the task list requires.
  if (Object.prototype.hasOwnProperty.call(mapping, path)) {
    return mapping[path];
  }
  for (const pattern in mapping) {
    if (pathMatchesPattern(path, pattern)) {
      return mapping[pattern];
    }
  }
  return {};
}

export function pathMatchesPattern(path, pattern) {
  const escapedPattern = pattern.replace(/[-[\]{}()+?.,\\^$|#\s]/g, '\\$&');

  const paramPattern = escapedPattern.replace(/\/:\w+/g, '(?:/[^/]+)?');

  const finalPattern = paramPattern.replace(/\*/g, '.*');

  const regexPattern = new RegExp(`^(?:/[^/]+)?${finalPattern}$`);
  return regexPattern.test(path);
}

/**
 * Where a user lands at login, and where the denial page's "Go to main page"
 * sends them.
 *
 * The order below is the preference — cockpit before calendar, calendar before
 * the Email Center — but a candidate only wins if `checkUserAccessToPath` says
 * the user can actually open it. Hand-written conditions drifted from
 * `routeToPermissionMap` twice: the calendar branch keyed on the partner's
 * scheduler flag alone, and the leads branch still keys on `leads_view` without
 * the `lead_management` module, so every scheduler-only user whose role happened
 * to include `leads_view` was sent to /crm/table and met "You do not have
 * sufficient permission" — and the denial page resolves back through here, so
 * the two bounced off each other with nowhere to land. Validating the candidate
 * against the same map the wrapper uses is what keeps the two in step.
 */
export function getFirstRoute(
  user: BackofficeUserModel,
  selectedSolution: string,
) {
  const isManagerOrPartner = hasAccess(
    user,
    [PERMISSIONS.Analytics.VIEW, PERMISSIONS.BUSINESS_PARTNER],
    { asterisk: true },
  );

  const isLeadManagementSolution =
    selectedSolution === SOLUTION_KEYS.LEAD_MANAGEMENT;
  const isLeadGenerationSolution =
    selectedSolution === SOLUTION_KEYS.LEAD_GENERATION;

  const candidates = [
    // Managers land on the Sales cockpit; the lead generation dashboard is the
    // same rank on the other solution.
    isManagerOrPartner && isLeadManagementSolution && ROUTE_PATHS.TEAM_OVERVIEW,
    isManagerOrPartner &&
      isLeadGenerationSolution &&
      ROUTE_PATHS.REPORTS_DASHBOARD,
    // Agents get their own daily dashboard.
    ROUTE_PATHS.MY_DAY,
    isLeadGenerationSolution && ROUTE_PATHS.LEAD_GENERATION_CUSTOMERS_ROOT,
    ROUTE_PATHS.LEADS,
    ROUTE_PATHS.CALENDAR,
    // Touchpoints-only roles land on the surface their permission is for.
    ROUTE_PATHS.EMAIL_CENTER,
  ].filter(Boolean) as string[];

  const reachable = candidates.find((path) =>
    checkUserAccessToPath(user, path),
  );

  // Global fallback: always reachable, so it is the honest end of the chain.
  return reachable ?? ROUTE_PATHS.PROFILE;
}
